import { Reducer } from "react";
import { type TabData } from "@utils/dashboard-storage";
import {
  TabErrors,
  DeleteConfirmationState,
  GroupDeleteConfirmationState,
  DraggedItem,
  DropPointer,
} from "./types";

export interface TabsState {
  localTabs: TabData[];
  tabErrors: TabErrors;
  newTabName: string;
  newTabError: string | null;
  isAddingNewTab: boolean;
  hoveredTabId: string | null;
  hoveredNewItem: boolean;
  dragged: DraggedItem | null;
  dragOver: DropPointer | null;
  deleteConfirmation: DeleteConfirmationState | null;
  groupDeleteConfirmation: GroupDeleteConfirmationState | null;
}

export type TabsAction =
  | { type: "SYNC_FROM_PROPS"; tabs: TabData[] }
  | { type: "SET_LOCAL_TABS"; tabs: TabData[] }
  | { type: "SET_NEW_TAB_NAME"; value: string }
  | { type: "SET_NEW_TAB_ERROR"; value: string | null }
  | { type: "START_ADD" }
  | { type: "CANCEL_ADD" }
  | { type: "ADD_OPTIMISTIC_TAB"; tab: TabData }
  | { type: "UPDATE_TAB_NAME"; id: string; label: string }
  | { type: "REVERT_TAB_NAME"; id: string; label: string }
  | { type: "SET_TAB_ERROR"; id: string; message: string }
  | { type: "CLEAR_TAB_ERROR"; id: string }
  | { type: "HOVER_TAB"; tabId: string | null }
  | { type: "HOVER_NEW_ITEM"; value: boolean }
  | { type: "DRAG_START"; dragged: DraggedItem }
  | { type: "DRAG_OVER"; target: DropPointer | null }
  | { type: "DRAG_END" }
  | {
      type: "OPEN_DELETE_CONFIRM";
      payload: {
        tabId: string;
        tabName: string;
        hasContent: boolean;
        affectedGroupName: string | null;
      };
    }
  | { type: "CLOSE_DELETE_CONFIRM" }
  | { type: "SET_DELETE_HAS_CONTENT"; hasContent: boolean }
  | { type: "OPEN_GROUP_DELETE_CONFIRM"; groupName: string }
  | { type: "CLOSE_GROUP_DELETE_CONFIRM" };

export const initialState = (tabs: TabData[]): TabsState => ({
  localTabs: tabs,
  tabErrors: {},
  newTabName: "",
  newTabError: null,
  isAddingNewTab: false,
  hoveredTabId: null,
  hoveredNewItem: false,
  dragged: null,
  dragOver: null,
  deleteConfirmation: null,
  groupDeleteConfirmation: null,
});

export const tabsReducer: Reducer<TabsState, TabsAction> = (state, action) => {
  switch (action.type) {
    case "SYNC_FROM_PROPS": {
      // Close add UI if we were adding and now have more tabs (new tab was created)
      const shouldCloseAdd =
        state.isAddingNewTab && action.tabs.length > state.localTabs.length;

      // Use only server tabs - no optimistic UI
      return {
        ...state,
        localTabs: action.tabs,
        isAddingNewTab: shouldCloseAdd ? false : state.isAddingNewTab,
        newTabName: shouldCloseAdd ? "" : state.newTabName,
      };
    }
    case "SET_LOCAL_TABS":
      return { ...state, localTabs: action.tabs };
    case "SET_NEW_TAB_NAME":
      return {
        ...state,
        newTabName: action.value,
        newTabError: action.value.trim() ? null : state.newTabError,
      };
    case "SET_NEW_TAB_ERROR":
      return { ...state, newTabError: action.value };
    case "START_ADD":
      return { ...state, isAddingNewTab: true };
    case "CANCEL_ADD":
      return {
        ...state,
        isAddingNewTab: false,
        newTabName: "",
        newTabError: null,
      };
    case "ADD_OPTIMISTIC_TAB":
      // Keep the add UI open while waiting for server response
      // The input will be disabled and skeleton will show
      return {
        ...state,
        isAddingNewTab: true, // Keep it open
        newTabName: state.newTabName, // Keep the name
        newTabError: null,
      };
    case "UPDATE_TAB_NAME": {
      const tabErrors = { ...state.tabErrors };
      if (action.label.trim()) delete tabErrors[action.id];
      return {
        ...state,
        tabErrors,
        localTabs: state.localTabs.map((t) =>
          t.id === action.id ? { ...t, label: action.label } : t,
        ),
      };
    }
    case "REVERT_TAB_NAME":
      return {
        ...state,
        localTabs: state.localTabs.map((t) =>
          t.id === action.id ? { ...t, label: action.label } : t,
        ),
      };
    case "SET_TAB_ERROR":
      return {
        ...state,
        tabErrors: { ...state.tabErrors, [action.id]: action.message },
      };
    case "CLEAR_TAB_ERROR": {
      const { [action.id]: _, ...rest } = state.tabErrors;
      return { ...state, tabErrors: rest };
    }
    case "HOVER_TAB":
      return { ...state, hoveredTabId: action.tabId };
    case "HOVER_NEW_ITEM":
      return { ...state, hoveredNewItem: action.value };
    case "DRAG_START":
      return { ...state, dragged: action.dragged };
    case "DRAG_OVER":
      return { ...state, dragOver: action.target };
    case "DRAG_END":
      return { ...state, dragged: null, dragOver: null };
    case "OPEN_DELETE_CONFIRM":
      return { ...state, deleteConfirmation: { ...action.payload } };
    case "CLOSE_DELETE_CONFIRM":
      return { ...state, deleteConfirmation: null };
    case "SET_DELETE_HAS_CONTENT":
      return state.deleteConfirmation
        ? {
            ...state,
            deleteConfirmation: {
              ...state.deleteConfirmation,
              hasContent: action.hasContent,
            },
          }
        : state;
    case "OPEN_GROUP_DELETE_CONFIRM":
      return { ...state, groupDeleteConfirmation: { groupName: action.groupName } };
    case "CLOSE_GROUP_DELETE_CONFIRM":
      return { ...state, groupDeleteConfirmation: null };
    default:
      return state;
  }
};
